import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { IExpressionEvaluatorContext } from "../../ExpressionsModule";
import { VariableNames } from "../Constants/VariableNames";
import convertValueForSave from "../Components/DataDisplay/convertValueForSave";
import hasPropertyValueChanged from "../Components/DataDisplay/hasPropertyValueChanged";
import { DataDisplayModel } from "../Models/DataDisplayModel";
import { PropertyDisplayFormat } from "../Models/PropertyDisplayFormat";
import getNestedPropertyValue from "../../common/getNestedPropertyValue";
import toNestedObject from "../../common/toNestedObject";
import { formatPhoneNumber } from "../Components/DataDisplay/phoneNumberFormatting";

interface UseBulkEditProps {
    component: DataDisplayModel;
    data: any;
    context: IExpressionEvaluatorContext;
    errorHandler: (error: any) => void;
    dataRefresh: () => Promise<void>;
    setIsUpdating: (isUpdating: boolean) => void;
    phoneNumberFormat?: string;
}

interface UseBulkEditReturn {
    isBulkEditMode: boolean;
    bulkEditValues: Record<string, any>;
    originalBulkValues: Record<string, any>;
    hasBulkChanges: boolean;
    handleEnterBulkEditMode: () => void;
    handleCancelBulkEdit: () => void;
    handleSaveBulkEdit: () => Promise<void>;
    handleBulkEditChange: (field: string, value: any) => void;
}

/**
 * Custom hook to manage bulk edit mode for DataDisplay component
 */
export default function useBulkEdit({
    component,
    data,
    context,
    errorHandler,
    dataRefresh,
    setIsUpdating,
    phoneNumberFormat,
}: UseBulkEditProps): UseBulkEditReturn {
    const [isBulkEditMode, setIsBulkEditMode] = useState<boolean>(false);
    const [bulkEditValues, setBulkEditValues] = useState<Record<string, any>>({});
    const [originalBulkValues, setOriginalBulkValues] = useState<Record<string, any>>({});

    const handleEnterBulkEditMode = useCallback(() => {
        if (!component.UpdateData || !data) return;

        const initialValues: Record<string, any> = {};
        const originalValues: Record<string, any> = {};

        component.Properties.forEach((prop) => {
            // Only include properties without their own UpdateData and without Lookup
            if (!prop.UpdateData && !prop.Lookup && prop.Field) {
                const fieldValue = getNestedPropertyValue(prop.Field, data);

                // Parse date/datetime values to Dayjs
                if (prop.Format === PropertyDisplayFormat.Date || prop.Format === PropertyDisplayFormat.DateTime) {
                    initialValues[prop.Field] = fieldValue ? dayjs(fieldValue) : null;
                    originalValues[prop.Field] = fieldValue ? dayjs(fieldValue) : null;
                } else if (prop.Format === PropertyDisplayFormat.Percent) {
                    const pct = fieldValue !== null && fieldValue !== undefined ? parseFloat(fieldValue) : "";
                    initialValues[prop.Field] = pct;
                    originalValues[prop.Field] = pct;
                } else if (prop.Format === PropertyDisplayFormat.PhoneLink) {
                    const formattedPhoneNumber = formatPhoneNumber(fieldValue, phoneNumberFormat);
                    initialValues[prop.Field] = formattedPhoneNumber;
                    originalValues[prop.Field] = formattedPhoneNumber;
                } else {
                    initialValues[prop.Field] = fieldValue !== null && fieldValue !== undefined ? fieldValue : "";
                    originalValues[prop.Field] = fieldValue !== null && fieldValue !== undefined ? fieldValue : "";
                }
            }
        });

        setBulkEditValues(initialValues);
        setOriginalBulkValues(originalValues);
        setIsBulkEditMode(true);
    }, [component, data, phoneNumberFormat]);

    const handleCancelBulkEdit = useCallback(() => {
        setIsBulkEditMode(false);
        setBulkEditValues({});
        setOriginalBulkValues({});
    }, []);

    const handleSaveBulkEdit = useCallback(async () => {
        if (!component.UpdateData) return;

        setIsUpdating(true);
        try {
            const eventContext = context.CreateChild();
            const changedFields: Record<string, any> = {};

            // Only include fields that have changed
            Object.keys(bulkEditValues).forEach((field) => {
                const currentValue = bulkEditValues[field];
                const originalValue = originalBulkValues[field];
                const prop = component.Properties.find((p) => p.Field === field);

                const hasChanged = hasPropertyValueChanged(currentValue, originalValue, prop?.Format);

                if (hasChanged) {
                    changedFields[field] = convertValueForSave(currentValue, prop?.Format);
                }
            });

            // Only proceed if there are changes
            if (Object.keys(changedFields).length > 0) {
                eventContext.VariableScope.set(VariableNames.Event, toNestedObject(changedFields));
                await eventContext.Evaluator.evaluate(component.UpdateData, eventContext);

                // Update variables in the context variable scope
                for (const field in changedFields) {
                    context.Evaluator.assign(component.GetData, context, changedFields[field], field.split("."));
                }

                // Refresh data after update
                await dataRefresh();
            }

            // Exit bulk edit mode
            setIsBulkEditMode(false);
            setBulkEditValues({});
            setOriginalBulkValues({});
        } catch (error) {
            errorHandler(error);
        } finally {
            setIsUpdating(false);
        }
    }, [bulkEditValues, originalBulkValues, component, context, errorHandler, dataRefresh, setIsUpdating]);

    const handleBulkEditChange = useCallback((field: string, value: any) => {
        setBulkEditValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    // Check if there are any changed fields in bulk edit mode
    const hasBulkChanges = Object.keys(bulkEditValues).some((field) => {
        const currentValue = bulkEditValues[field];
        const originalValue = originalBulkValues[field];
        const prop = component.Properties.find((p) => p.Field === field);
        return hasPropertyValueChanged(currentValue, originalValue, prop?.Format);
    });

    return {
        isBulkEditMode,
        bulkEditValues,
        originalBulkValues,
        hasBulkChanges,
        handleEnterBulkEditMode,
        handleCancelBulkEdit,
        handleSaveBulkEdit,
        handleBulkEditChange,
    };
}
